import { IS_GATEWAY_V2 } from "@/contract/gateway-version";

/**
 * `buyNft` changed arity between market generations, so it cannot live in one
 * shared ABI.
 *
 * V1: buyNft(address parent, PurchaseItem[] items)
 * V2: buyNft(address referrer, address parent, PurchaseItem[] items)
 *
 * Gateway V2 splits placement into PlacementInput{referrer, parent} — the
 * referrer earns the referral cut, the parent is the binary-tree position.
 * The UI collects them as two independent fields (see usePlacementResolution,
 * useReferrerFieldValidation, useParentFieldValidation) and passes both
 * through as-typed — never defaulted to one another here.
 *
 * The selectors differ, so calling the V2 shape against a deployed V1 market
 * does not mis-execute — it reverts. Which is why this is selected rather than
 * always sending three arguments.
 */

const PURCHASE_ITEMS = {
  components: [
    { internalType: "address", name: "seller", type: "address" },
    { internalType: "uint256", name: "sv", type: "uint256" },
    { internalType: "uint256", name: "bv", type: "uint256" },
    { internalType: "uint256", name: "paymentTokenId", type: "uint256" },
    { internalType: "uint256", name: "buyerAmount", type: "uint256" },
    { internalType: "string", name: "tokenUri", type: "string" },
  ],
  name: "items",
  type: "tuple[]",
} as const;

export const BUY_NFT_ABI_V1 = [
  {
    inputs: [
      { internalType: "address", name: "parent", type: "address" },
      { internalType: "struct Market.PurchaseItem[]", ...PURCHASE_ITEMS },
    ],
    name: "buyNft",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
] as const;

export const BUY_NFT_ABI_V2 = [
  {
    inputs: [
      { internalType: "address", name: "referrer", type: "address" },
      { internalType: "address", name: "parent", type: "address" },
      { internalType: "struct MarketV2.PurchaseItem[]", ...PURCHASE_ITEMS },
    ],
    name: "buyNft",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
] as const;

export const BUY_NFT_ABI = (
  IS_GATEWAY_V2 ? BUY_NFT_ABI_V2 : BUY_NFT_ABI_V1
) as typeof BUY_NFT_ABI_V2;

export type PurchaseItem = {
  seller: `0x${string}`;
  sv: bigint;
  bv: bigint;
  paymentTokenId: bigint;
  buyerAmount: bigint;
  tokenUri: string;
};

/**
 * Placement arguments in the shape the configured market expects. Returned as
 * a plain array so the caller appends `items` and stays arity-agnostic.
 */
export const buyNftPlacementArgs = (
  parentAddress: `0x${string}`,
  referrerAddress: `0x${string}`,
): `0x${string}`[] =>
  IS_GATEWAY_V2 ? [referrerAddress, parentAddress] : [parentAddress];
